
import { formatCurrency, formatDate, getParticipantName, getLinkedParticipants } from './utils.mjs';
import { createMonetaryInput } from './monetaryInput.mjs';
import { appendModalFooterButton } from './uiHelpers.mjs';
import router from './router.mjs';
import t from 'virtual:i18n';

/**
 * Round a monetary value to the correct number of decimal places for the given currency.
 * Uses the ISO 4217 standard (JPY → 0, USD → 2, KWD → 3, etc.).
 * This avoids floating-point drift bugs like 1200.005 rounding to 1200.01 for JPY.
 * @param {number} amount
 * @param {string} currency
 * @returns {number}
 */
function roundToCurrencyPrecision(amount, currency) {
    try {
        const digits = new Intl.NumberFormat('en-US', { style: 'currency', currency })
            .resolvedOptions()
            .maximumFractionDigits;
        const factor = Math.pow(10, digits);
        return Math.round(amount * factor) / factor;
    } catch {
        // Fallback: 2 decimal places
        return Math.round(amount * 100) / 100;
    }
}

/**
 * Render the Debt Details screen
 * @param {HTMLElement} parentEl 
 * @param {Group} group 
 * @param {number} payeeGroupIndex - Creditor
 * @param {number} payerGroupIndex - Debtor
 */
export function renderDebtDetails(parentEl, group, payeeGroupIndex, payerGroupIndex) {
    parentEl.innerHTML = '';

    // Header / Back Button provided by main layout, but we might want a title if not present.
    // For now, assuming parentEl is the main container.

    // 1. Find the DebtEntry
    let debtEntry = null;
    let debtEntries = [];
    if (group.summary && group.summary.debtEntries) {
        debtEntries = group.summary.debtEntries;
    } else if (group.settlement && group.settlement.debtEntries) {
        debtEntries = group.settlement.debtEntries;
    }

    debtEntry = debtEntries.find(e => e.creditor === payeeGroupIndex && e.debtor === payerGroupIndex);

    if (!debtEntry) {
        renderSquaredAway(parentEl);
        return;
    }

    const totalDebt = debtEntry.amount;

    // 2. Resolve Participants
    const payerIndices = getLinkedParticipants(group, payerGroupIndex);
    const payeeIndices = getLinkedParticipants(group, payeeGroupIndex);
    const payerSet = new Set(payerIndices);
    const payeeSet = new Set(payeeIndices);

    const payerName = getParticipantName(group, payerGroupIndex);
    const payeeName = getParticipantName(group, payeeGroupIndex);

    // 3. Check if transactions are minimized — skip expense items if so
    const isMinimized = group.groupSettings?.simplify === true;

    // 3. Filter Items (Expenses & Payments)
    const items = [];

    // Process Expenses
    if (group.expenses) {
        group.expenses.forEach(expense => {
            if (!expense.debt) return;

            const expenseParticipants = expense.participants || [];
            const pLen = expenseParticipants.length;

            expense.debt.forEach((debtAmount, i) => {
                if (debtAmount > 0) {
                    // Matrix logic: row=payee, col=payer. Payee OWES Payer.
                    const payeeIdx = expenseParticipants[Math.floor(i / pLen)];
                    const payerIdx = expenseParticipants[i % pLen];

                    const isPayeeInDebtorGroup = payerSet.has(payeeIdx); // Payee (Ower) is in Debtor Group
                    const isPayerInCreditorGroup = payeeSet.has(payerIdx); // Payer (Owed) is in Creditor Group

                    const isPayeeInCreditorGroup = payeeSet.has(payeeIdx); // Payee (Ower) is in Creditor Group
                    const isPayerInDebtorGroup = payerSet.has(payerIdx); // Payer (Owed) is in Debtor Group

                    const debtorName = group.participants[payeeIdx];
                    const creditorName = group.participants[payerIdx];

                    if (isPayeeInDebtorGroup && isPayerInCreditorGroup) {
                        // Debtor Group member owes Creditor Group member.
                        // This increases the main debt.
                        items.push({
                            type: 'expense',
                            date: expense.date,
                            description: expense.description,
                            amount: debtAmount,
                            subtitle: `${debtorName} \u2794 ${creditorName}`,
                            isCredit: false, // It's a debt
                            expense: expense,
                            payee: payeeIdx,
                            payer: payerIdx
                        });
                    } else if (isPayeeInCreditorGroup && isPayerInDebtorGroup) {
                        // Creditor Group member owes Debtor Group member.
                        // (Debtor paid for Creditor).
                        // This reduces the main debt.
                        items.push({
                            type: 'expense',
                            date: expense.date,
                            description: expense.description,
                            amount: debtAmount,
                            subtitle: `${debtorName} \u2794 ${creditorName}`,
                            isCredit: true, // It's a credit (reduces debt)
                            expense: expense,
                            payee: payeeIdx,
                            payer: payerIdx
                        });
                    }
                }
            });
        });
    }

    // Process Direct Payments (SettledEntries)
    if (group.settlement && group.settlement.settledEntries) {
        group.settlement.settledEntries.forEach(entry => {
            // Entry: payer -> payee
            // Note: In SettledEntry, 'payer' is the one who GAVE MONEY. 'payee' is RECEIVER.
            // Wait, standard SplitWise/SplitPal logic:
            // SettledEntry: Payer (Index) paid Payee (Index).

            if (entry.payee === payeeGroupIndex && entry.payer === payerGroupIndex) {
                // Debtor Group paid Creditor Group.
                // Reduces Debt.
                items.push({
                    type: 'payment',
                    date: entry.date,
                    amount: entry.amount,
                    description: t.payment,
                    subtitle: t.paidTo.replace('{payer}', payerName).replace('{payee}', payeeName),
                    isCredit: true,
                    comment: entry.comment,
                    entryId: entry.id
                });
            }
            if (entry.payee === payerGroupIndex && entry.payer === payeeGroupIndex) {
                // Creditor Group paid Debtor Group.
                // Increases Debt (net).
                items.push({
                    type: 'payment',
                    date: entry.date,
                    amount: entry.amount,
                    description: t.payment,
                    subtitle: t.paidTo.replace('{payer}', payeeName).replace('{payee}', payerName),
                    isCredit: false,
                    comment: entry.comment,
                    entryId: entry.id
                });
            }
        });
    }

    // Split into expenses and payments, sort each by date (reverse chronological)
    const expenseItems = items.filter(i => i.type === 'expense');
    const paymentItems = items.filter(i => i.type === 'payment');
    expenseItems.sort((a, b) => new Date(b.date) - new Date(a.date));
    paymentItems.sort((a, b) => new Date(b.date) - new Date(a.date));

    // 4. Render
    const container = document.createElement('div');
    container.className = 'debt-details-container';
    Object.assign(container.style, {
        padding: '0 16px 60px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    });

    // Title / summary — show "All Squared Away!" when debt is zero
    const header = document.createElement('div');
    header.style.padding = '12px 0';
    header.style.textAlign = 'center';
    if (totalDebt === 0) {
        header.innerHTML = `
            <div style="display:inline-flex;align-items:center;gap:10px;margin-top:4px;">
                <svg viewBox="0 0 24 24" style="width:28px;height:28px;fill:#00897B;flex-shrink:0;">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span style="font-size:18px;font-weight:600;color:#00897B;">${t.allSquaredAway}</span>
            </div>
        `;
    } else {
        header.innerHTML = `
            <h2 style="margin:0; font-size: 16px; color: #666; font-weight: 500;">${t.payeeOwesPayer.replace('{payeeStr}', payerName).replace('{payerStr}', payeeName)}</h2>
            <h1 style="margin:8px 0; font-size: 32px; color: #1a1a1a; font-weight: 700;">${formatCurrency(totalDebt, group.currency)}</h1>
        `;
    }

    /** Renders a single list item element for an expense or payment entry */
    function buildItemEl(item) {
        const isPayment = item.type === 'payment';
        const isCredit = item.isCredit;
        const isItemized = item.type === 'expense' && item.expense && item.expense.expenseType === 'ITEMIZED';

        const itemEl = document.createElement('div');
        itemEl.className = 'list-item';
        Object.assign(itemEl.style, {
            cursor: isItemized ? 'pointer' : 'default',
            backgroundColor: '#fff',
            borderBottom: '1px solid #f0f0f0',
            marginBottom: isItemized ? '0' : '8px'
        });

        let icon = '';
        if (isPayment) {
            icon = `<div class="icon-container" style="background-color: #e0f2f1">
                        <svg class="icon" viewBox="0 0 24 24" style="fill: #00897B">
                           <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                    </div>`;
        } else if (isCredit) {
            icon = `<div class="icon-container" style="background-color: #e0f2f1">
                        <svg class="icon" viewBox="0 0 24 24" style="fill: #00897B">
                            <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
                        </svg>
                    </div>`;
        } else {
            icon = `<div class="icon-container" style="background-color: #f5f5f5">
                        <svg class="icon" viewBox="0 0 24 24" style="fill: #757575">
                           <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
                        </svg>
                    </div>`;
        }

        const amountColor = isCredit ? '#00897B' : '#000';

        let subtitleHTML = item.subtitle;
        if (isPayment && item.comment && item.comment !== 'null') {
            subtitleHTML += `<br><span style="font-style: italic; color: #999; font-size: 13px;">${item.comment}</span>`;
        }
        // Show date for payment entries inside the settled section
        if (isPayment && item.date) {
            subtitleHTML += `<br><span style="color: #aaa; font-size: 12px;">${formatDate(item.date)}</span>`;
        }

        function formatMonetaryValue(amount, currencyCode, baseCurrency, rate) {
            if (baseCurrency && rate && rate !== 1 && currencyCode !== baseCurrency) {
                return `${formatCurrency(amount / rate, baseCurrency)} (${formatCurrency(amount, currencyCode)})`;
            }
            return formatCurrency(amount, currencyCode);
        }

        let amountHTML = `${isCredit ? '— ' : ''}${formatCurrency(item.amount, group.currency)}`;
        if (isItemized) {
            const shouldExpandByDefault = expenseItems.length === 1;
            const initialRotation = shouldExpandByDefault ? 'rotate(0deg)' : 'rotate(-90deg)';
            amountHTML = `
                <div style="display: flex; align-items: center; gap: 4px;">
                    <span>${isCredit ? '— ' : ''}${formatCurrency(item.amount, group.currency)}</span>
                    <svg class="expand-chevron" viewBox="0 0 24 24" style="width:20px;height:20px;fill:#888;transition:transform 0.25s;transform:${initialRotation};"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/></svg>
                </div>
            `;
        }

        itemEl.innerHTML = `
            <div class="list-item-leading">${icon}</div>
            <div class="list-item-content">
                <div class="list-item-title" style="font-weight: 500; font-size: 15px;">${item.description}</div>
                <div class="list-item-subtitle" style="color: ${isPayment ? '#666' : '#888'};">${subtitleHTML}</div>
            </div>
            <div class="list-item-trailing" style="display: flex; flex-direction: column; align-items: flex-end; gap: 4px;">
                <div class="list-item-amount" style="color: ${amountColor}; font-weight: 600;">
                    ${amountHTML}
                </div>
            </div>
        `;

        if (isPayment && item.entryId) {
            const trailing = itemEl.querySelector('.list-item-trailing');
            const undoButton = document.createElement('button');
            undoButton.textContent = t.undo;
            Object.assign(undoButton.style, {
                marginTop: '8px',
                padding: '6px 14px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#1D192B',
                backgroundColor: '#E8DEF8',
                border: 'none',
                borderRadius: '24px',
                cursor: 'pointer',
                transition: 'background-color 0.2s, transform 0.2s'
            });
            undoButton.onmouseover = () => {
                undoButton.style.backgroundColor = '#D8CEF0';
            };
            undoButton.onmouseout = () => {
                undoButton.style.backgroundColor = '#E8DEF8';
            };
            undoButton.onmousedown = () => undoButton.style.transform = 'scale(0.96)';
            undoButton.onmouseup = () => undoButton.style.transform = 'scale(1)';
            undoButton.onclick = async (e) => {
                e.stopPropagation();
                if (!confirm(t.areYouSure)) return;
                try {
                    const { deleteExpense } = await import('./api.mjs');
                    const updatedGroup = await deleteExpense(group.id, item.entryId);
                    const { cache } = await import('./main.mjs');
                    cache.group = updatedGroup;
                    router.navigate(window.location.pathname);
                } catch (error) {
                    console.error('Failed to undo payment:', error);
                    alert(t.failedToUndoPayment);
                }
            };
            trailing.appendChild(undoButton);
        }

        if (isItemized) {
            const containerWrap = document.createElement('div');
            containerWrap.className = 'itemized-expense-wrapper';
            containerWrap.style.cssText = 'display: flex; flex-direction: column; margin-bottom: 8px; background-color: #fff; border-radius: 12px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08); overflow: hidden;';

            Object.assign(itemEl.style, {
                boxShadow: 'none',
                marginBottom: '0',
                borderRadius: '12px 12px 0 0'
            });

            containerWrap.appendChild(itemEl);

            const shouldExpandByDefault = expenseItems.length === 1;
            const breakdownContainer = document.createElement('div');
            breakdownContainer.style.cssText = shouldExpandByDefault
                ? 'display: block; overflow: hidden; background-color: #fafafa; border-top: 1px solid #f0f0f0;'
                : 'display: none; overflow: hidden; background-color: #fafafa; border-top: 1px solid #f0f0f0;';

            const expense = item.expense;
            const payee = item.payee;
            const payer = item.payer;
            
            const payeeIndex = expense.participants ? expense.participants.indexOf(payee) : -1;
            const payeePortion = payeeIndex >= 0 ? expense.portions[payeeIndex] : 0;
            
            let payeeAlreadyPaid = 0;
            const payerPayeeIndex = expense.payers ? expense.payers.indexOf(payee) : -1;
            if (payerPayeeIndex >= 0) {
                payeeAlreadyPaid = expense.payerAmounts[payerPayeeIndex];
            }

            const itemsList = expense.itemizedData?.items || [];
            const additionalCharges = expense.itemizedData?.additionalCharges || [];
            
            let totalCharges = 0;
            additionalCharges.forEach(charge => {
                const val = charge.isDiscount ? -Math.abs(charge.value) : charge.value;
                totalCharges += val;
            });

            let totalItemPrice = 0;
            itemsList.forEach(i => {
                totalItemPrice += i.price || 0;
            });

            const breakdownItems = [];
            let extraCharges = 0;
            itemsList.forEach(itemObj => {
                if (itemObj.assignees) {
                    itemObj.assignees.forEach(itemAssignee => {
                        if (itemAssignee.participants) {
                            itemAssignee.participants.forEach(participant => {
                                if (participant === payee) {
                                    let amount = itemObj.price * (itemAssignee.quantity / itemObj.quantity);
                                    extraCharges += (amount / (totalItemPrice || 1)) * totalCharges;
                                    let quantityStr = itemAssignee.quantity.toString();
                                    if (itemAssignee.participants.length > 1) {
                                        quantityStr += ` (1/${itemAssignee.participants.length})`;
                                        amount /= itemAssignee.participants.length;
                                    }

                                    breakdownItems.push({
                                        quantity: quantityStr,
                                        name: itemObj.name || '',
                                        amount: amount
                                    });
                                }
                            });
                        }
                    });
                }
            });

            if (extraCharges !== 0) {
                breakdownItems.push({
                    quantity: '',
                    name: t.taxesTipsDiscounts || 'Taxes / Tips / Discounts',
                    amount: extraCharges
                });
            }

            if (payeeAlreadyPaid > 0) {
                breakdownItems.push({
                    quantity: '',
                    name: t.alreadyPaid || 'Already Paid',
                    amount: -payeeAlreadyPaid
                });
            }

            breakdownItems.push({
                quantity: '',
                name: t.total || 'Total',
                amount: payeePortion - payeeAlreadyPaid,
                isTotal: true
            });

            breakdownItems.forEach(child => {
                const rowEl = document.createElement('div');
                rowEl.className = 'breakdown-row';
                Object.assign(rowEl.style, {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: child.isTotal ? '12px 16px 12px 32px' : '8px 16px 8px 32px',
                    fontSize: '13px',
                    color: '#666',
                    borderTop: child.isTotal ? '1px dashed #ddd' : '1px solid #f9f9f9',
                    fontWeight: child.isTotal ? 'bold' : 'normal'
                });

                const leftCol = document.createElement('div');
                leftCol.style.cssText = 'display:flex; align-items:center; gap:8px;';
                if (child.quantity) {
                    leftCol.innerHTML = `<span style="font-weight:600; color:#555; min-width:32px;">${child.quantity}</span> <span style="color:#555;">${child.name}</span>`;
                } else {
                    leftCol.innerHTML = `<span style="color:#555;">${child.name}</span>`;
                }

                const rightCol = document.createElement('div');
                rightCol.style.cssText = 'font-weight: 500; text-align: right;';
                if (child.isTotal) {
                    rightCol.style.color = '#333';
                    rightCol.textContent = formatMonetaryValue(child.amount, expense.currency, group.currency, expense.rate);
                } else {
                    rightCol.textContent = formatCurrency(child.amount, expense.currency);
                }

                rowEl.appendChild(leftCol);
                rowEl.appendChild(rightCol);
                breakdownContainer.appendChild(rowEl);
            });

            containerWrap.appendChild(breakdownContainer);

            let expanded = shouldExpandByDefault;
            itemEl.addEventListener('click', (e) => {
                expanded = !expanded;
                const chevronSvg = itemEl.querySelector('.expand-chevron');
                if (expanded) {
                    breakdownContainer.style.display = 'block';
                    if (chevronSvg) chevronSvg.style.transform = 'rotate(0deg)';
                } else {
                    breakdownContainer.style.display = 'none';
                    if (chevronSvg) chevronSvg.style.transform = 'rotate(-90deg)';
                }
            });

            return containerWrap;
        }

        return itemEl;
    }

    // List: expense items only
    const list = document.createElement('div');
    expenseItems.forEach(item => list.appendChild(buildItemEl(item)));

    // "Paid Back" collapsible section for settled entries
    let paidBackSection = null;
    if (paymentItems.length > 0) {
        const totalPaid = paymentItems.reduce((sum, p) => sum + p.amount, 0);
        // All credit payments reduce the main debt; show as — amount like Flutter
        const netPaid = paymentItems
            .reduce((sum, p) => sum + (p.isCredit ? p.amount : -p.amount), 0);

        paidBackSection = document.createElement('div');
        paidBackSection.style.marginTop = '8px';

        // ── Header row (toggle) ────────────────────────────────────────────
        const sectionHeader = document.createElement('div');
        Object.assign(sectionHeader.style, {
            display: 'flex',
            alignItems: 'center',
            padding: '14px 16px',
            backgroundColor: 'transparent',
            borderBottom: 'none',
            cursor: 'pointer',
            userSelect: 'none',
        });

        // Left: label + subtitle
        const sectionLeft = document.createElement('div');
        sectionLeft.style.flex = '1';
        sectionLeft.innerHTML = `
            <div style="font-weight: 600; font-size: 15px; color: #1a1a1a;">${t.paidBack}</div>
            <div style="font-size: 13px; color: #888; margin-top: 2px;">${payerName} \u2794 ${payeeName}</div>
        `;

        // Right: amount + chevron
        const sectionRight = document.createElement('div');
        Object.assign(sectionRight.style, {
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
        });

        const amountLabel = document.createElement('span');
        amountLabel.style.cssText = 'font-size: 14px; color: #555; font-weight: 600;';
        amountLabel.textContent = `\u2212\u00A0${formatCurrency(Math.abs(netPaid), group.currency)}`;

        // Chevron SVG (points down when expanded, up when collapsed)
        const chevron = document.createElement('span');
        chevron.innerHTML = `<svg viewBox="0 0 24 24" style="width:20px;height:20px;fill:#888;transition:transform 0.25s;transform:rotate(0deg)" class="paid-back-chevron"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/></svg>`;

        sectionRight.appendChild(amountLabel);
        sectionRight.appendChild(chevron);

        sectionHeader.appendChild(sectionLeft);
        sectionHeader.appendChild(sectionRight);

        // ── Entries container (expanded by default) ───────────────────────
        const entriesContainer = document.createElement('div');
        entriesContainer.style.overflow = 'hidden';
        // Start expanded
        let expanded = true;

        paymentItems.forEach(item => {
            const el = buildItemEl(item);
            // Slightly indent the cards inside the group
            el.style.backgroundColor = '#fafafa';
            entriesContainer.appendChild(el);
        });

        // Toggle logic
        sectionHeader.addEventListener('click', () => {
            expanded = !expanded;
            const chevronSvg = chevron.querySelector('svg');
            if (expanded) {
                entriesContainer.style.display = '';
                chevronSvg.style.transform = 'rotate(0deg)';
            } else {
                entriesContainer.style.display = 'none';
                chevronSvg.style.transform = 'rotate(-90deg)';
            }
        });

        paidBackSection.appendChild(sectionHeader);
        paidBackSection.appendChild(entriesContainer);
    }

    // 5. Payback Card (Async)
    const paybackContainer = document.createElement('div');
    paybackContainer.className = 'payback-card-container';

    // "Minimized" info banner — shown instead of expense items when simplify is on
    let minimizedBanner = null;
    if (isMinimized) {
        minimizedBanner = document.createElement('div');
        Object.assign(minimizedBanner.style, {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            margin: '4px 0 12px',
            padding: '12px 16px',
            backgroundColor: '#e8f4f8',
            borderRadius: '10px',
            border: '1px solid #b3e0f7',
        });
        minimizedBanner.innerHTML = `
            <svg viewBox="0 0 24 24" style="width:18px;height:18px;fill:#0288d1;flex-shrink:0;">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
            <span style="font-size:13.5px;color:#01579b;line-height:1.45;">${t.detailsAreNotAvailableWhenDebtTransactions}</span>
        `;
    }

    // Layout
    container.innerHTML = ''; // Reset
    container.appendChild(header);            // 1. Summary header (owes total) — always on top
    if (minimizedBanner) {
        container.appendChild(minimizedBanner);  // 2a. Info banner (minimized mode)
    } else {
        container.appendChild(list);              // 2b. Expense items (normal mode)
    }
    if (paidBackSection) {
        container.appendChild(paidBackSection); // 3. Paid Back group (if any)
    }
    container.appendChild(paybackContainer);  // 4. Payback card placeholder

    parentEl.appendChild(container);

    // Logic to fetch user data and render card
    if (totalDebt > 0 && group.assignedUsers) {
        const payeeIndices = getLinkedParticipants(group, payeeGroupIndex);
        let paybackUserId = null;

        for (let i = 0; i < payeeIndices.length; i++) {
            const pIndex = payeeIndices[i];
            const assignment = group.assignedUsers.find(u => u.participant === pIndex);
            if (assignment && assignment.userId) {
                paybackUserId = assignment.userId;
                break;
            }
        }

        if (paybackUserId) {
            import('./api.mjs').then(module => {
                module.getPublicUserData(paybackUserId).then(userData => {
                    if (userData && userData.paybackMethods && userData.paybackMethods.length > 0) {
                        renderPaybackCard(paybackContainer, userData, payeeName);
                    }
                });
            });
        }
    }

    // FAB Button (Mark as Paid)
    let mainFab = null;
    if (totalDebt > 0) {
        import('./components.mjs').then(({ createFAB, Icons }) => {
            mainFab = createFAB({
                text: t.markPaid,
                icon: Icons.checkmark,
                onClick: () => {
                    // Hide the main FAB when opening the modal
                    if (mainFab) {
                        mainFab.style.display = 'none';
                    }
                    openMarkPaidModal(group, payeeGroupIndex, payerGroupIndex, totalDebt, payeeName, mainFab);
                }
            });

            container.appendChild(mainFab);

            // Hide global footer when Mark Paid button is visible
            const globalFooter = document.getElementById('global-footer');
            if (globalFooter) {
                globalFooter.style.display = 'none';
            }
        });
    }
}

/**
 * Open the Mark Paid modal
 * @param {Group} group 
 * @param {number} payeeGroupIndex 
 * @param {number} payerGroupIndex 
 * @param {number} totalDebt 
 * @param {string} payeeName 
 */
async function openMarkPaidModal(group, payeeGroupIndex, payerGroupIndex, totalDebt, payeeName, mainFab = null) {
    const { renderModal, closeModal } = await import('./ui.mjs');
    const { addSettledEntry } = await import('./api.mjs');
    const { formatCurrency } = await import('./utils.mjs');
    const { createFAB, Icons } = await import('./components.mjs');

    // Round to currency-correct decimal places once — backend floats can have
    // drift (e.g. 2369.5900000000001 for USD, or 1200.005 for JPY).
    // Using a cents-scale (×100) rounding for JPY incorrectly turns 1200.005
    // into 1200.01, making the exact-amount comparison fail.
    const maxAmount = roundToCurrencyPrecision(totalDebt, group.currency || 'USD');

    // State
    let amount = maxAmount;
    let comment = '';
    let date = new Date().toISOString().substring(0, 10);
    let isValid = true;

    // Create modal body
    const modalBody = document.createElement('div');
    Object.assign(modalBody.style, {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        padding: '0 4px'
    });

    // Amount Section
    const amountSection = document.createElement('div');

    const amountLabel = document.createElement('label');
    amountLabel.textContent = t.amount;
    Object.assign(amountLabel.style, {
        display: 'block',
        fontSize: '12px',
        color: '#666',
        marginBottom: '4px'
    });
    amountSection.appendChild(amountLabel);

    const amountInputContainer = document.createElement('div');
    Object.assign(amountInputContainer.style, {
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
    });

    const currencySymbol = document.createElement('span');
    currencySymbol.textContent = group.currency || 'USD';
    Object.assign(currencySymbol.style, {
        position: 'absolute',
        left: '16px',
        fontSize: '14px',
        color: '#666',
        fontWeight: '500',
        zIndex: '1',
        pointerEvents: 'none'
    });

    const amountInput = createMonetaryInput({
        id: 'mark-paid-amount',
        value: maxAmount,
        placeholder: '0.00',
        currency: group.currency || 'USD',
        className: ''
    });

    Object.assign(amountInput.style, {
        width: '100%',
        padding: '12px',
        paddingLeft: '60px',
        fontSize: '16px',
        fontWeight: 'bold',
        border: '1px solid #ccc',
        borderRadius: '8px',
        outline: 'none',
        boxSizing: 'border-box'
    });

    const helperText = document.createElement('div');
    Object.assign(helperText.style, {
        fontSize: '12px',
        marginTop: '6px',
        minHeight: '18px'
    });

    const updateHelperText = () => {
        const inputAmount = roundToCurrencyPrecision(amountInput.getRawValue(), group.currency || 'USD');
        if (inputAmount > maxAmount) {
            helperText.textContent = t.cannotBeMoreThanOwed;
            helperText.style.color = '#d32f2f';
            amountInput.style.borderColor = '#d32f2f';
            isValid = false;
        } else if (inputAmount < maxAmount && inputAmount > 0) {
            const remaining = roundToCurrencyPrecision(maxAmount - inputAmount, group.currency || 'USD');
            helperText.textContent = `${payerName} ${t.wouldStillOwe.replace('{value}', formatCurrency(remaining, group.currency))}`;
            helperText.style.color = '#666';
            amountInput.style.borderColor = '#ccc';
            isValid = true;
        } else if (inputAmount === maxAmount) {
            helperText.textContent = '';
            amountInput.style.borderColor = '#ccc';
            isValid = true;
        } else {
            helperText.textContent = '';
            amountInput.style.borderColor = '#ccc';
            isValid = inputAmount > 0;
        }
        updateSubmitButton();
    };

    amountInput.addEventListener('input', (e) => {
        amount = amountInput.getRawValue();
        updateHelperText();
    });

    amountInputContainer.appendChild(currencySymbol);
    amountInputContainer.appendChild(amountInput);
    amountSection.appendChild(amountInputContainer);
    amountSection.appendChild(helperText);
    modalBody.appendChild(amountSection);

    // Comment Section
    const commentSection = document.createElement('div');

    const commentLabel = document.createElement('label');
    commentLabel.textContent = `${t.comment} (${t.optional})`;
    Object.assign(commentLabel.style, {
        display: 'block',
        fontSize: '12px',
        color: '#666',
        marginBottom: '4px'
    });
    commentSection.appendChild(commentLabel);

    const commentInput = document.createElement('input');
    commentInput.type = 'text';
    commentInput.placeholder = t.optional;
    Object.assign(commentInput.style, {
        width: '100%',
        padding: '12px',
        fontSize: '14px',
        border: '1px solid #ccc',
        borderRadius: '8px',
        outline: 'none',
        boxSizing: 'border-box'
    });
    commentInput.addEventListener('input', (e) => {
        comment = e.target.value;
    });

    commentSection.appendChild(commentInput);
    modalBody.appendChild(commentSection);

    // Date Section
    const dateSection = document.createElement('div');

    const dateLabel = document.createElement('label');
    dateLabel.textContent = t.date;
    Object.assign(dateLabel.style, {
        display: 'block',
        fontSize: '12px',
        color: '#666',
        marginBottom: '4px'
    });
    dateSection.appendChild(dateLabel);

    const dateInput = document.createElement('input');
    dateInput.type = 'date';
    dateInput.value = date;
    Object.assign(dateInput.style, {
        width: '100%',
        padding: '12px',
        fontSize: '14px',
        border: '1px solid #ccc',
        borderRadius: '8px',
        outline: 'none',
        boxSizing: 'border-box'
    });
    dateInput.addEventListener('change', (e) => {
        date = e.target.value;
    });

    dateSection.appendChild(dateInput);
    modalBody.appendChild(dateSection);


    // Create modal
    const modal = renderModal({
        anchorEl: document.body,
        title: t.markPaid,
        body: modalBody,
        buttons: [],
        onClose: () => {
            // Show the main FAB again when modal closes
            if (mainFab) {
                mainFab.style.display = 'flex';
            }
        }
    });

    // Create fixed button at bottom
    const { createPrimaryButton } = await import('./components.mjs');

    const submitButton = createPrimaryButton({
        text: t.markPaid,
        disabled: true,
        onClick: async () => {
            if (!isValid || amount <= 0 || amount > maxAmount) return;

            try {
                const input = {
                    amount: amount,
                    payer: payerGroupIndex,
                    payee: payeeGroupIndex,
                    date: date,
                    comment: comment || null
                };

                const updatedGroup = await addSettledEntry(group.id, input);

                // Update the SPA cache then re-render via router (no full reload)
                const { cache } = await import('./main.mjs');
                cache.group = updatedGroup;
                closeModal(modal);
                router.navigate(window.location.pathname);
            } catch (error) {
                console.error('Failed to mark as paid:', error);
                alert(t.failedToMarkPaid);
            }
        }
    });

    const updateSubmitButton = () => {
        const isButtonValid = isValid && amount > 0 && amount <= maxAmount;
        submitButton.disabled = !isButtonValid;
        submitButton.style.opacity = isButtonValid ? '1' : '0.5';
        submitButton.style.cursor = isButtonValid ? 'pointer' : 'not-allowed';
    };

    appendModalFooterButton(modal, submitButton);

    // Center the title text
    const modalTitle = modal.querySelector('.modal-title');
    if (modalTitle) {
        modalTitle.style.textAlign = 'center';
    }

    // Initial validation
    updateHelperText();
    updateSubmitButton();


}

/**
 * Shows a "Leaving SplitPal" confirmation dialog before opening an external payment link.
 * Mirrors Flutter's ExternalNavigationDialog.showAndLaunch().
 * @param {string} url - The external payment deep link URL
 */
function showExternalNavigationDialog(url) {
    // Overlay
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
        position: 'fixed',
        inset: '0',
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: '10000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        boxSizing: 'border-box',
    });

    // Dialog
    const dialog = document.createElement('div');
    Object.assign(dialog.style, {
        backgroundColor: '#fff',
        borderRadius: '28px',
        padding: '24px',
        maxWidth: '380px',
        width: '100%',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        fontFamily: 'inherit',
        animation: 'dialogPop 0.18s cubic-bezier(0.34,1.56,0.64,1)',
    });

    // Inject keyframe once
    if (!document.getElementById('ext-nav-style')) {
        const s = document.createElement('style');
        s.id = 'ext-nav-style';
        s.textContent = `@keyframes dialogPop { from { transform: scale(0.88); opacity: 0; } to { transform: scale(1); opacity: 1; } }`;
        document.head.appendChild(s);
    }

    const title = document.createElement('div');
    title.textContent = t.leavingSplitPal;
    Object.assign(title.style, {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1D192B',
        marginBottom: '12px',
    });

    const body = document.createElement('div');
    body.textContent = t.externalNavigationWarning;
    Object.assign(body.style, {
        fontSize: '14px',
        color: '#444',
        lineHeight: '1.55',
        marginBottom: '24px',
    });

    const buttons = document.createElement('div');
    Object.assign(buttons.style, {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
    });

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = t.cancel;
    Object.assign(cancelBtn.style, {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: '600',
        color: 'var(--color-primary)',
        background: 'transparent',
        border: 'none',
        borderRadius: '20px',
        cursor: 'pointer',
        transition: 'background 0.15s',
    });
    cancelBtn.onmouseenter = () => cancelBtn.style.background = '#f3eeff';
    cancelBtn.onmouseleave = () => cancelBtn.style.background = 'transparent';
    cancelBtn.onclick = () => document.body.removeChild(overlay);

    const confirmBtn = document.createElement('button');
    confirmBtn.textContent = t.iUnderstand;
    Object.assign(confirmBtn.style, {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#fff',
        background: 'var(--color-primary)',
        border: 'none',
        borderRadius: '20px',
        cursor: 'pointer',
        transition: 'background 0.15s',
    });
    confirmBtn.onmouseenter = () => confirmBtn.style.background = '#7965AF';
    confirmBtn.onmouseleave = () => confirmBtn.style.background = 'var(--color-primary)';
    confirmBtn.onclick = () => {
        document.body.removeChild(overlay);
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    // Close on overlay click
    overlay.onclick = (e) => {
        if (e.target === overlay) document.body.removeChild(overlay);
    };

    buttons.appendChild(cancelBtn);
    buttons.appendChild(confirmBtn);
    dialog.appendChild(title);
    dialog.appendChild(body);
    dialog.appendChild(buttons);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Focus confirm for keyboard accessibility
    setTimeout(() => confirmBtn.focus(), 50);
}

function renderPaybackCard(container, userData, payeeName) {

    const card = document.createElement('div');
    Object.assign(card.style, {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '20px 20px 16px',
        marginTop: '20px',
        marginBottom: '100px', // Extra margin for FAB
        color: '#1D192B',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        border: '1px solid #f0f0f0'
    });

    // Header: Avatar & Name
    const header = document.createElement('div');
    header.style.marginBottom = '16px';
    header.style.display = 'flex';
    header.style.alignItems = 'center';
    header.style.gap = '16px';

    const avatar = document.createElement('div');
    Object.assign(avatar.style, {
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        backgroundColor: 'var(--color-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        fontWeight: 'bold',
        color: 'white',
        flexShrink: '0'
    });
    const initials = (userData.preferredName || userData.username || payeeName).substring(0, 1).toUpperCase();
    avatar.innerText = initials;

    const titleText = document.createElement('div');
    const nameLabel = document.createElement('div');
    nameLabel.innerText = userData.preferredName || userData.username || payeeName;
    nameLabel.style.cssText = 'font-weight:700;font-size:16px;color:#1D192B;';
    const subLabel = document.createElement('div');
    subLabel.innerText = t.youCanPayMeBackVia;
    subLabel.style.cssText = 'font-size:13px;color:#666;margin-top:3px;';
    titleText.appendChild(nameLabel);
    titleText.appendChild(subLabel);

    header.appendChild(avatar);
    header.appendChild(titleText);
    card.appendChild(header);

    // Payback methods
    userData.paybackMethods.forEach(method => {
        const methodType = method.type; // 'VENMO', 'PAYPAL', 'CASH_APP', 'UPI', 'PLAIN_TEXT'
        const methodData = method.data || '';

        // Build deep link exactly as the Flutter app does
        let deepLink = null;
        if (methodData) {
            const encodedUsername = encodeURIComponent(methodData);
            switch (methodType) {
                case 'PAYPAL':
                    deepLink = `https://paypal.me/${encodedUsername}`;
                    break;
                case 'CASH_APP':
                    deepLink = `https://cash.app/${encodedUsername}`;
                    break;
                case 'VENMO':
                    deepLink = `https://venmo.com/payment-link?recipients=${encodedUsername}&txn=pay`;
                    break;
                case 'UPI':
                    deepLink = `upi://pay?pa=${encodedUsername}`;
                    break;
                default:
                    deepLink = null;
            }
        }

        // Divider
        const dividerEl = document.createElement('div');
        dividerEl.style.cssText = 'height:1px;background:#f0f0f0;margin:0;';
        card.appendChild(dividerEl);

        // PLAIN_TEXT: show inline text instructions
        if (methodType === 'PLAIN_TEXT') {
            const plainRow = document.createElement('div');
            plainRow.style.cssText = 'display:flex;align-items:flex-start;gap:14px;padding:14px 4px;';
            const iconWrap = document.createElement('div');
            iconWrap.style.cssText = 'width:36px;height:36px;flex-shrink:0;display:flex;align-items:center;justify-content:center;';
            iconWrap.innerHTML = `<svg viewBox="0 0 24 24" width="28" height="28" fill="#555"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z"/></svg>`;
            const textWrap = document.createElement('div');
            const textTitle = document.createElement('div');
            textTitle.textContent = t.textInstructions;
            textTitle.style.cssText = 'font-weight:600;font-size:15px;color:#1D192B;';
            const textBody = document.createElement('div');
            textBody.textContent = methodData;
            textBody.style.cssText = 'font-size:13px;color:#666;margin-top:4px;line-height:1.5;';
            textWrap.appendChild(textTitle);
            textWrap.appendChild(textBody);
            plainRow.appendChild(iconWrap);
            plainRow.appendChild(textWrap);
            card.appendChild(plainRow);
            return;
        }

        // Branded row with logo + arrow
        const row = document.createElement('div');
        row.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 4px 6px;
            border-radius: 10px;
            margin: 2px 0;
            cursor: ${deepLink ? 'pointer' : 'default'};
            transition: background-color 0.15s;
        `;

        // Logo
        const logoWrap = document.createElement('div');
        logoWrap.style.cssText = 'height:52px;display:flex;align-items:center;padding:0 6px;min-width:100px;overflow:visible;';

        // Venmo: wide wordmark — use width not height to prevent clipping
        const VENMO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 37" width="110" style="display:block;overflow:visible">
  <path d="M35.4 1.5c1.4 2.3 2.1 4.7 2.1 7.8 0 9.7-8.3 22.4-15.1 31.2H6.2L0 3 13.5 1.7l3.3 26.2c3.1-5 6.8-12.8 6.8-18.1 0-2.9-.5-4.9-1.3-6.5z" fill="#008cff"/>
  <path d="M53 17.1c2.5 0 8.7-1.1 8.7-4.7 0-1.7-1.2-2.6-2.6-2.6-2.5 0-5.7 3-6.1 7.3zm-.3 7c0 4.3 2.4 6 5.6 6 3.5 0 6.8-.9 11.1-3l-1.6 11.1c-3.1 1.5-7.8 2.5-12.4 2.5-11.7 0-15.9-7.1-15.9-16 0-11.5 6.8-23.7 20.9-23.7 7.7 0 12.1 4.3 12.1 10.4 0 9.7-12.5 12.7-19.7 12.7z" fill="#008cff"/>
  <path d="M109.5 9.7c0 1.4-.2 3.5-.4 4.8L105.1 40H91.9l3.7-23.4c.1-.6.3-1.9.3-2.6 0-1.7-1.1-2.1-2.3-2.1-1.7 0-3.4.8-4.5 1.4L85 40H71.8l6-38.3h11.4l.1 3.1c2.7-1.8 6.2-3.7 11.3-3.7 6.7 0 9 3.4 9 8.5z" fill="#008cff"/>
  <path d="M149 5.5c3.8-2.7 7.3-4.2 12.2-4.2 6.7 0 9.1 3.4 9.1 8.5 0 1.4-.2 3.5-.4 4.8L165.8 40h-13.1l3.8-23.9c.1-.6.2-1.4.2-1.9 0-1.9-1.1-2.3-2.3-2.3-1.6 0-3.3.7-4.5 1.4L145.9 40H133l3.8-23.9c.1-.6.2-1.4.2-1.9 0-1.9-1.1-2.3-2.3-2.3-1.7 0-3.4.8-4.5 1.4L126 40h-13.2l6-38.3h11.3l.4 3.2c2.6-1.9 6.2-3.8 10.9-3.8 4.1 0 6.8 1.8 8.2 4.2z" fill="#008cff"/>
  <path d="M196.3 16.6c0-3.1-.8-5.3-3.1-5.3-5.2 0-6.2 9.2-6.2 13.8 0 3.6 1 5.8 3.3 5.8 4.9 0 6-9.7 6-14.3zm-22.7 8c0-12.1 6.4-23.4 21.1-23.4 11.1 0 15.1 6.5 15.1 15.6 0 11.9-6.3 24.3-21.4 24.3-11.1 0-14.8-7.3-14.8-16.5z" fill="#008cff"/>
</svg>`;

        const PAYPAL_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 124 33" height="26" style="display:block">
  <path fill="#253B80" d="M46.2 6.7h-6.8c-.5 0-.9.3-.9.8L35.7 25.1c-.1.3.2.7.6.7h3.3c.5 0 .9-.3.9-.8l.8-4.7c.1-.5.5-.8.9-.8h2.2c4.5 0 7.1-2.2 7.8-6.5.3-1.9 0-3.4-.9-4.4C50.2 7.4 48.5 6.7 46.2 6.7zM47 13.2c-.4 2.5-2.2 2.5-4.1 2.5h-1l.7-4.6c.05-.3.29-.5.56-.5h.5c1.2 0 2.4 0 3 .7.4.4.5 1 .34 1.9z"/>
  <path fill="#253B80" d="M66.7 13.1h-3.3c-.28 0-.52.2-.56.5l-.15.9-.23-.33c-.7-1-2.3-1.4-3.9-1.4-3.6 0-6.7 2.7-7.3 6.6-.3 1.9.1 3.8 1.2 5 1 1.2 2.4 1.7 4.1 1.7 2.9 0 4.5-1.9 4.5-1.9l-.1.9c-.1.35.2.66.56.66H65c.47 0 .87-.34.94-.8l1.77-11.21c.08-.35-.19-.66-.54-.66zM62.1 19.4c-.3 1.9-1.8 3.1-3.7 3.1-.95 0-1.7-.3-2.2-.9-.48-.57-.67-1.39-.51-2.3.3-1.85 1.8-3.15 3.67-3.15.93 0 1.69.31 2.18.89.49.59.69 1.41.56 2.3z"/>
  <path fill="#253B80" d="M84.1 13.1h-3.3c-.31 0-.61.16-.79.42l-4.54 6.68-1.92-6.42c-.12-.4-.49-.68-.91-.68h-3.23c-.39 0-.67.38-.54.75l3.62 10.64-3.41 4.81c-.27.38 0 .9.47.9h3.28c.31 0 .6-.15.78-.41L84.56 13.97c.27-.38.01-.87-.46-.87z"/>
  <path fill="#179BD7" d="M95 6.7h-6.84c-.47 0-.865.34-.94.8l-2.77 17.54c-.05.35.21.66.56.66h3.51c.33 0 .61-.24.66-.56l.79-4.97c.07-.46.47-.8.94-.8h2.16c4.51 0 7.11-2.18 7.79-6.5.31-1.89.01-3.37-.87-4.42C99 7.4 97.3 6.7 95 6.7zM95.8 13.2c-.37 2.45-2.25 2.45-4.06 2.45h-1.03l.73-4.58c.04-.28.28-.48.56-.48h.47c1.24 0 2.4 0 3 .7.37.4.48 1.01.33 1.9z"/>
  <path fill="#179BD7" d="M115.4 13.1h-3.27c-.28 0-.52.2-.56.5l-.15.9-.23-.33c-.7-1-2.29-1.37-3.87-1.37-3.62 0-6.71 2.74-7.31 6.59-.31 1.91.13 3.75 1.22 5.03 1 1.18 2.43 1.67 4.13 1.67 2.91 0 4.53-1.87 4.53-1.87l-.15.91c-.05.35.21.66.56.66h2.95c.47 0 .86-.34.94-.8l1.77-11.21c.07-.35-.2-.66-.56-.66zM110.9 19.4c-.32 1.88-1.8 3.13-3.7 3.13-.95 0-1.71-.3-2.2-.88-.48-.58-.67-1.39-.51-2.3.3-1.86 1.81-3.15 3.67-3.15.93 0 1.69.31 2.18.89.49.59.69 1.41.55 2.3z"/>
  <path fill="#179BD7" d="M119.3 7.2l-2.81 17.86c-.05.35.21.66.56.66h2.82c.47 0 .87-.34.94-.8L123.6 7.4c.05-.35-.21-.66-.56-.66h-3.16c-.28 0-.52.2-.56.48z"/>
  <path fill="#253B80" d="M7.27 29.15l.52-3.32-1.16-.03H1.06L4.93 1.29C4.94 1.22 4.98 1.15 5.04 1.1c.06-.05.13-.08.21-.08h9.38c3.11 0 5.26.65 6.38 1.93.53.6.86 1.23 1.02 1.92.17.72.17 1.59 0 2.64v.68l.53.3c.44.24.79.5 1.06.81.45.51.74 1.17.86 1.94.13.8.09 1.74-.12 2.81-.24 1.23-.63 2.3-1.15 3.18-.48.81-1.1 1.48-1.83 2-.7.49-1.52.87-2.46 1.11-.9.24-1.94.35-3.07.35h-.73c-.52 0-1.03.19-1.43.52-.4.34-.66.81-.74 1.33l-.06.3-.92 5.85-.04.22c-.01.07-.03.1-.06.12-.02.02-.06.03-.1.03H7.27z"/>
  <path fill="#179BD7" d="M23.05 7.67c-.03.18-.06.36-.1.55-1.24 6.35-5.47 8.55-10.87 8.55H9.33c-.66 0-1.22.48-1.32 1.13L6.6 26.83 6.2 29.37c-.07.43.26.81.7.81h4.88c.58 0 1.07-.42 1.16-.99l.05-.25.92-5.83.06-.32c.09-.57.58-.99 1.16-.99h.73c4.73 0 8.43-1.92 9.51-7.48.45-2.32.22-4.26-.98-5.62-.34-.38-.78-.7-1.3-.98z"/>
  <path fill="#222D65" d="M21.75 7.15c-.19-.05-.38-.1-.58-.15-.2-.04-.41-.08-.62-.12-.74-.12-1.55-.18-2.43-.18h-7.35c-.18 0-.35.04-.51.12-.31.14-.56.46-.62.86L8.05 17.6l-.05.29c.1-.65.66-1.13 1.32-1.13h2.75c5.41 0 9.64-2.19 10.87-8.54.04-.19.07-.38.1-.55-.31-.17-.65-.31-1.02-.43-.09-.03-.18-.06-.27-.09z"/>
  <path fill="#253B80" d="M9.62 7.7c.06-.39.31-.71.65-.88.16-.07.33-.11.51-.11h7.35c.87 0 1.68.06 2.43.18.21.03.42.07.62.12.2.04.39.09.58.15.09.03.19.06.28.09.37.12.7.26 1.02.43.37-2.35 0-3.95-1.27-5.39C20.38.68 17.85 0 14.62 0H5.24C4.58 0 4.02.48 3.91 1.13L.01 25.9c-.08.49.3.93.8.93H6.6L8.05 17.6l1.57-9.9z"/>
</svg>`;

        // Cash App: green square icon — needs explicit width+height so rounded corners render
        const CASHAPP_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="40" height="40" style="display:block;border-radius:10px">
  <g fill-rule="nonzero">
    <path d="M41.7 0c6.4 0 9.6 0 13.1 1.1a13.6 13.6 0 0 1 8.1 8.1C64 12.7 64 15.9 64 22.31v19.37c0 6.42 0 9.64-1.1 13.1a13.6 13.6 0 0 1-8.1 8.1C51.3 64 48.1 64 41.7 64H22.3c-6.42 0-9.64 0-13.1-1.1a13.6 13.6 0 0 1-8.1-8.1C0 51.3 0 48.1 0 41.69V22.3c0-6.42 0-9.64 1.1-13.1a13.6 13.6 0 0 1 8.1-8.1C12.7 0 15.9 0 22.3 0h19.4z" fill="#00D632"/>
    <path d="M42.47 23.8c.5.5 1.33.5 1.8 0l2.5-2.6c.53-.5.5-1.4-.06-1.94a19.73 19.73 0 0 0-6.72-3.84l.79-3.8c.17-.83-.45-1.61-1.28-1.61h-4.84a1.32 1.32 0 0 0-1.28 1.06l-.7 3.38c-6.44.33-11.9 3.6-11.9 10.3 0 5.8 4.51 8.29 9.28 10 4.51 1.72 6.9 2.36 6.9 4.78 0 2.49-2.38 3.95-5.9 3.95-3.2 0-6.56-1.07-9.16-3.68a1.3 1.3 0 0 0-1.84 0l-2.7 2.7a1.36 1.36 0 0 0 0 1.92c2.1 2.07 4.76 3.57 7.792 4.4l-.74 3.57c-.17.83.44 1.6 1.27 1.61l4.85.04a1.32 1.32 0 0 0 1.3-1.06l.7-3.39C40.28 49.07 45 44.8 45 38.57c0-5.74-4.7-8.16-10.4-10.13-3.26-1.21-6.08-2.04-6.08-4.53 0-2.42 2.63-3.38 5.27-3.38 3.36 0 6.59 1.39 8.7 3.29z" fill="#FFF"/>
  </g>
</svg>`;

        const UPI_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="40" height="40" fill-rule="evenodd" style="display:block">
  <path d="M55.05 32.542L38.715 0l-18.15 64z" fill="#097939"/>
  <path d="M43.433 32.542L27.1 0 8.95 64z" fill="#ed752e"/>
</svg>`;

        switch (methodType) {
            case 'VENMO': logoWrap.innerHTML = VENMO_SVG; break;
            case 'PAYPAL': logoWrap.innerHTML = PAYPAL_SVG; break;
            case 'CASH_APP': logoWrap.innerHTML = CASHAPP_SVG; break;
            case 'UPI': logoWrap.innerHTML = UPI_SVG; break;
            default:
                logoWrap.innerHTML = `<span style="font-size:12px;font-weight:700;color:#555;padding:4px 8px;background:#eee;border-radius:6px;">${methodType}</span>`;
        }

        // Arrow
        const arrow = document.createElement('span');
        arrow.innerHTML = '&#8250;';
        arrow.style.cssText = 'font-size:28px;color:#bbb;line-height:1;padding-right:4px;transition:color 0.15s;';

        row.appendChild(logoWrap);
        row.appendChild(arrow);

        if (deepLink) {
            row.onclick = () => showExternalNavigationDialog(deepLink);
            row.addEventListener('mouseenter', () => {
                row.style.backgroundColor = '#f5f5f5';
                arrow.style.color = 'var(--color-primary)';
            });
            row.addEventListener('mouseleave', () => {
                row.style.backgroundColor = 'transparent';
                arrow.style.color = '#bbb';
            });
        }

        card.appendChild(row);
    });

    // Disclaimer
    const disclaimer = document.createElement('div');
    disclaimer.style.cssText = 'font-size:11px;color:#888;margin-top:18px;line-height:1.5;padding:0 4px;';
    const termsLink = `<a href="https://splitpal.app/terms" target="_blank" style="color:var(--color-primary);text-decoration:underline;">${t.termsOfService}</a>`;
    disclaimer.innerHTML = `<strong>${t.paybackDisclaimer}</strong><br>${t.paybackDisclaimerText.replace('{termsLink}', termsLink)}`;
    card.appendChild(disclaimer);

    container.appendChild(card);
}

function renderSquaredAway(parentEl) {
    parentEl.innerHTML = `
        <div style="text-align: center; padding: 40px; color: #888;">
            <h2>${t.allSquaredAway}</h2>
            <p>${t.noDebtsFound}</p>
        </div>
    `;
}
